<script setup>
import { computed, ref } from 'vue'

// Mock data
const chartData = ref([
  { name: 'AVRIL', activity: 6, guides: 4.5 },
  { name: 'MAI', activity: 6, guides: 4.5 },
  { name: 'JUIN', activity: 8, guides: 7 },
  { name: 'JUILLET', activity: 3.8, guides: 1.2 },
])

// Calculate max value from data for dynamic scaling
const dataMax = computed(() => {
  const allValues = chartData.value.flatMap(item => [
    item.activity,
    item.guides,
  ])
  return Math.max(...allValues)
})

// Round the max value up to a nice number for the y-axis
const roundedMax = computed(() => {
  const max = dataMax.value
  // Round up to next multiple of 2 if small, or 5 if larger
  if (max <= 10) {
    return Math.ceil(max / 2) * 2
  }
  else {
    return Math.ceil(max / 5) * 5
  }
})

// Generate y-axis scale values
const yAxisScale = computed(() => {
  const max = roundedMax.value
  const increment = max / 4
  return [max, max - increment, max - increment * 2, max - increment * 3, 0]
})

const maxHeight = 200 // Maximum height of bars in pixels

// Calculate height based on data value
function calculateHeight(value) {
  return (value / roundedMax.value) * maxHeight
}
</script>

<template>
  <div class="bg-white rounded-2xl p-6 shadow-sm">
    <!-- Chart Header -->
    <div class="flex justify-between items-start mb-4">
      <div>
        <div class="text-gray-400 text-sm">
          Graphique
        </div>
        <h2 class="text-gray-800 text-xl font-bold">
          Évolution de votre pratique
        </h2>
      </div>
      <div class="flex flex-col space-y-2">
        <div class="flex items-center">
          <div class="w-3 h-3 rounded-full bg-green-400 mr-2" />
          <span class="text-sm text-gray-500">Activité complétée</span>
        </div>
        <div class="flex items-center">
          <div class="w-3 h-3 rounded-full bg-purple-500 mr-2" />
          <span class="text-sm text-gray-500">Guide généré</span>
        </div>
      </div>
    </div>

    <!-- Chart Container -->
    <div class="relative h-64 mt-10">
      <!-- Y-axis Labels -->
      <div
        class="hidden absolute left-0 top-0 h-full flex flex-col justify-between text-gray-400 text-sm pr-4"
      >
        <div>{{ yAxisScale[0] }}</div>
        <div>{{ yAxisScale[1] }}</div>
        <div>{{ yAxisScale[2] }}</div>
        <div>{{ yAxisScale[3] }}</div>
        <div>{{ yAxisScale[4] }}</div>
      </div>

      <!-- Chart Grid Lines -->
      <div class="ml-6 h-full flex flex-col justify-between">
        <div
          v-for="i in 5"
          :key="i"
          class="border-t border-gray-200 border-dashed w-full h-0"
        />
      </div>

      <!-- Bars Container -->
      <div
        class="absolute left-10 right-0 top-0 bottom-0 flex items-end justify-around"
      >
        <div
          v-for="(month, index) in chartData"
          :key="index"
          class="flex items-end space-x-4"
        >
          <!-- Activity Bar -->
          <div class="relative flex flex-col items-center">
            <div
              class="w-8 bg-green-400 rounded-t-lg"
              :style="{ height: `${calculateHeight(month.activity)}px` }"
            />
          </div>

          <!-- Guide Bar -->
          <div class="relative flex flex-col items-center">
            <div
              class="w-8 bg-purple-500 rounded-t-lg"
              :style="{ height: `${calculateHeight(month.guides)}px` }"
            />
          </div>
        </div>
      </div>

      <!-- X-axis Labels -->
      <div
        class="absolute left-10 right-0 bottom-0 transform translate-y-8 flex justify-around"
      >
        <div
          v-for="(month, index) in chartData"
          :key="index"
          class="flex-1 text-center text-gray-400"
        >
          {{ month.name }}
        </div>
      </div>
    </div>
  </div>
</template>
